<!-- eslint-disable vue/multi-word-component-names -->
<template>
	<base-dropdown
		align="right"
		class="user-menu"
		:class="{ 'user-menu--with-username': showUserName }"
		@toggle-menu="toggleMenu"
	>
		<template #default="{ toggleOpen }">
			<div class="user-menu__group">
				<span v-if="showUserName" class="user-menu__welcome">
					Hi
					<!-- eslint-disable-next-line prettier/prettier -->
					<current-user tag="span" :filter="firstNameFilter" />
					!
				</span>

				<button class="user-menu__activator" @click="toggleOpen">
					<div class="user-menu__icon" :class="{ 'user-menu__icon--close': isOpen }" />
				</button>
			</div>
		</template>

		<template #dropdown>
			<div class="user-menu__menu">
				<base-menu-list>
					<base-menu-item :to="{ name: 'settings' }" label="User Settings" />

					<template v-if="$can('access', 'ADMIN_PANEL')">
						<base-menu-item v-if="isAdminSection" :to="{ name: 'dashboard' }" label="Dashboard" />

						<base-menu-item v-if="!isAdminSection" :to="{ name: 'admin' }" label="Administration" />
					</template>

					<template v-if="$can('access', 'PRODUCTION_PANEL')">
						<base-menu-item
							v-if="isProductionSection"
							:to="{ name: 'dashboard' }"
							label="Dashboard"
						/>

						<base-menu-item
							v-if="!isProductionSection"
							:to="{ name: 'Production' }"
							label="Production"
						/>
					</template>

					<base-menu-item
						button
						:to="{ name: 'terms-and-conditions' }"
						label="Terms and conditions"
					/>
					<base-menu-item button class="user-menu__logout" label="Logout" @click="logout" />
				</base-menu-list>
			</div>
		</template>
	</base-dropdown>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import CurrentUser from '@/components/CurrentUser.vue';
import { authActions } from '@/store/modules/auth';

export default defineComponent({
	components: {
		CurrentUser,
	},

	props: {
		showUserName: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			isOpen: false,
			firstNameFilter: (fullName: string) => fullName.split(' ')[0],
		};
	},

	computed: {
		isAdminSection(): boolean {
			return this.$route.path.includes('admin');
		},

		isProductionSection(): boolean {
			return this.$route.path.includes('production');
		},
	},

	methods: {
		async logout() {
			await authActions.logout();
		},

		toggleMenu(toggleState: boolean) {
			this.isOpen = toggleState;
		},
	},
});
</script>

<style lang="scss">
.user-menu {
	$self: &;

	margin: -0.7rem 0 -1.5rem;
	padding: 0.7rem 0 1.5rem;

	@mixin is-dropdown-open {
		#{$self}.base-dropdown--is-open & {
			@content;
		}
	}

	&.base-dropdown--is-open {
		z-index: 1000;
	}

	&.base-dropdown,
	.base-dropdown__control {
		min-width: auto;
	}

	&--with-username {
		&.base-dropdown,
		.base-dropdown__control {
			min-width: 17.4rem;
		}
	}

	&__group {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;

		@include is-dropdown-open {
			justify-content: flex-end;
		}
	}

	&__welcome {
		padding-right: 0.4rem;
		font-size: $font-size-small;
		transition: all 250ms $easeOutQuad;
		transition-property: transform;
		will-change: transform;
		backface-visibility: hidden;

		@include is-dropdown-open {
			width: 12.9rem;
			position: absolute;
			right: 4.5rem;
			transform: scale(1.1) translate(0.5rem, 0);

			@include max-lg-and-portrait {
				visibility: visible;
				opacity: 1;
			}
		}

		@include max-lg-and-portrait {
			font-size: 1.5rem;
			visibility: hidden;
			transition-property: transform, opacity;
			opacity: 0;
		}
	}

	&__activator {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 4.8rem;
		height: 4.8rem;
		position: relative;
		cursor: pointer;
		transition: all 0.2s ease-in-out;
		border: 1px solid $color-black;
		border-radius: 0.8rem;

		@include max-lg-and-portrait {
			width: 4.4rem;
			height: 4.4rem;
		}

		&:hover {
			#{$self}__icon {
				&::before {
					transform: translateY(-2px);
				}

				&::after {
					transform: translateY(2px);
				}
			}
		}
	}

	&__icon {
		width: 1.8rem;
		height: 2px;
		margin: 1.2rem;
		position: relative;
		transition: transform 0.2s;
		background-color: $color-black;

		&::before,
		&::after {
			content: '';
			width: 1.8rem;
			height: 2px;
			position: absolute;
			left: 0;
			transition: all 0.2s;
			background-color: $color-black;
		}

		&::before {
			top: -5px;
		}

		&::after {
			top: 5px;
		}

		&--close {
			width: 0;
			left: -20%;

			&::before,
			&::after {
				top: 0;
			}

			&::before {
				transform: rotate(45deg) translateY(50%) scaleX(0);
				animation: animation-name-auto('open-before') 250ms $easeOutQuad forwards;

				@keyframes #{animation-name-auto('open-before')} {
					from {
						transform: rotate(45deg) translateY(50%) scaleX(0);
					}

					to {
						transform: rotate(45deg) translateY(50%) scaleX(1);
					}
				}
			}

			&::after {
				transform: rotate(-45deg) scaleX(0);
				animation: animation-name-auto('open-after') 250ms 100ms $easeOutQuad forwards;

				@keyframes #{animation-name-auto('open-after')} {
					from {
						transform: rotate(-45deg) scaleX(0);
					}

					to {
						transform: rotate(-45deg) scaleX(1);
					}
				}
			}
		}
	}

	&__menu {
		.current-user {
			font-weight: 500;
		}

		.base-menu-item {
			margin-right: -1.5rem;
			margin-left: -1.5rem;
			font-size: $font-size-body;

			@include max-lg-and-portrait {
				font-size: 1.65rem;
			}

			a {
				text-decoration: none;
			}

			&__activator {
				position: relative;
				overflow: hidden;

				&--is-clickable {
					.base-menu-item__label {
						position: relative;
						z-index: 10;
					}

					&::before {
						content: '';
						display: block;
						width: 100%;
						height: 100%;
						position: absolute;
						top: 0;
						left: 0;
						transition: opacity 150ms ease-out;
						transform-origin: 50% 100%;
						opacity: 0;
						background: $color-light;
					}

					&:hover {
						.base-menu-item__label {
							@keyframes #{animation-name-auto('hover')} {
								100% {
									transform: translateY(0%);
									opacity: 1;
								}

								50% {
									transform: translateY(50%);
									opacity: 0;
								}

								51% {
									transform: translateY(-50%);
									opacity: 0;
								}

								100% {
									transform: translateY(0%);
									opacity: 1;
								}
							}
						}

						&::before {
							animation: animation-name-auto('hover') 200ms $easeOutQuad;
							opacity: 1;

							@keyframes #{animation-name-auto('hover')} {
								from {
									transform: scaleY(0.5) translateY(-100%);
									border-radius: 0 0 50% 50%;
								}

								to {
									transform: scaleY(1);
									border-radius: 0;
								}
							}
						}
					}
				}
			}
		}

		.base-menu-item__activator {
			padding-right: 1.5rem;
			padding-left: 1.5rem;
		}
	}

	&__logout {
		cursor: pointer;
		opacity: 0.8;
	}
}
</style>
